import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { findInviteeSlots } from '@/features/offers/offer-detail/helper/approverSlotMatching.helper'
import { OfferBookingStatus, PRApprovalStatus, PRApprovalType, PRStatus } from '@eapproval/offer-shared'

const usePRSubmissionEditRights = (
  submissionDetail,
  user,
  canEditPRSubmission = false,
  offerStatus,
  adminMode = false
) => {
  const approverDetails = submissionDetail?.approverDetails ?? []
  const currentUserId = user?._id || user?.id
  const currentUserGroup = user?.contributorGroup || user?.group

  const canEditPRSubmissionGeneralField = adminMode || canEditPRSubmission

  const approverDetailsWithApproverIndex = approverDetails.map((approverDetail, index) => ({
    ...approverDetail,
    approverIndex: index,
  }))

  const matchedApproverDetails = approverDetailsWithApproverIndex.filter((approverDetail) => {
    if (approverDetail.type === PRApprovalType.LOGISTIC) {
      return currentUserGroup === ContributorGroupType.LOGISTIC
    }

    if ([PRApprovalType.HSP_2].includes(approverDetail.type)) {
      return currentUserGroup === ContributorGroupType.PROCUREMENT_OFFICER
    }

    const approverUserId = approverDetail?.user?._id || approverDetail?.user || approverDetail?.userId
    const delegateeUserId =
      approverDetail?.delegationDetail?.delegatee?._id || approverDetail?.delegationDetail?.delegatee
    return approverUserId === currentUserId || delegateeUserId === currentUserId
  })

  const userApproverDetail =
    matchedApproverDetails.find((detail) => detail?.status === PRApprovalStatus.REQUIRED) ||
    matchedApproverDetails.find((detail) => detail?.status === PRApprovalStatus.PARKED) ||
    matchedApproverDetails[0]

  const HSP_2ApproverDetail = approverDetails.find((detail) => detail.type === PRApprovalType.HSP_2)

  // Same user can simultaneously hold more than one actionable approver slot on this
  // submission (reuses the same per-type matching as userApproverDetail above, but
  // resolves every matching slot instead of only the first).
  const inviteSlots = matchedApproverDetails
    .filter((detail) => [PRApprovalStatus.REQUIRED, PRApprovalStatus.PARKED].includes(detail?.status))
    .map((detail) => ({ approverIndex: detail.approverIndex, detail }))
  const replySlots = findInviteeSlots(approverDetailsWithApproverIndex, currentUserId)

  const showPRApprovalButtons =
    submissionDetail.status === PRStatus.SUBMITTED && userApproverDetail?.status === PRApprovalStatus.REQUIRED

  const showPROnHoldButtons =
    submissionDetail.status === PRStatus.SUBMITTED &&
    userApproverDetail?.type === PRApprovalType.HSP_2 &&
    userApproverDetail?.status === PRApprovalStatus.REQUIRED
  const showApproveOnHoldButton =
    submissionDetail.status === PRStatus.PARKED &&
    userApproverDetail?.type === PRApprovalType.HSP_2 &&
    userApproverDetail?.status === PRApprovalStatus.PARKED

  const showPRArchiveButton =
    [PRStatus.SUBMITTED, PRStatus.REJECTED, PRStatus.PARKED].includes(submissionDetail.status) && canEditPRSubmission
  const showPRDeleteButton = [PRStatus.DRAFT].includes(submissionDetail.status) && canEditPRSubmission

  const showPRSubmitButton = canEditPRSubmission && submissionDetail.status === PRStatus.DRAFT

  const showPRReSubmitButton = canEditPRSubmission && submissionDetail.status === PRStatus.REJECTED

  const showPRReplyButton = replySlots.length > 0

  const canUploadSubcontractorAgreement =
    adminMode ||
    (canEditPRSubmission && [PRApprovalType.HSP_2, PRApprovalType.HSP_1].includes(userApproverDetail?.type))
  const canEditSubcontractorAgreementRequired =
    adminMode ||
    (canEditPRSubmission && [PRApprovalType.HSP_2, PRApprovalType.HSP_1].includes(userApproverDetail?.type))

  return {
    canEditPRSubmissionGeneralField,
    showPRApprovalButtons,
    showPROnHoldButtons,
    showApproveOnHoldButton,
    showPRArchiveButton,
    showPRDeleteButton,
    showPRSubmitButton,
    showPRReSubmitButton,
    showPRReplyButton,

    canUploadSubcontractorAgreement,
    canEditSubcontractorAgreementRequired,
    approverDetail: userApproverDetail,
    inviteSlots,
    replySlots,
    canEditPRSubmissionQuantity: adminMode || canEditPRSubmission,
    canEditPRSubmissionTable: adminMode || (canEditPRSubmission && currentUserGroup === ContributorGroupType.LOGISTIC),
  }
}

export default usePRSubmissionEditRights
